'use client';

import type { ElementType } from 'react';
import { Link, cn } from '@skin-seoul/design-system';
import {
  getCartAddProduct,
  getCartProductPackLabel,
  getProductEffectivePrice,
  getPriceProductBasicInfo,
  type Currency,
  type Product,
  type VariationProduct,
} from '@skin-seoul/service';
import { extractPathname } from '@skin-seoul/utils';
import { CartDiscoveryCard } from './CartDiscoveryCard';
import { useCartCardTracking, type CartCardAttribution } from './CartAnalytics';

export function CartDiscoveryProductCard({
  product,
  currency,
  linkComponent,
  onProductClick,
  rating,
  onCartMutation,
  className,
  layout = 'compact',
  attribution,
}: {
  layout?: 'compact' | 'grid';
  className?: string;
  onCartMutation?: (mutation: Promise<boolean>) => void;
  rating?: { averageRate: number; totalReviews: number };
  product: Product | VariationProduct;
  currency: Currency;
  linkComponent?: ElementType;
  onProductClick: () => void;
  attribution?: CartCardAttribution;
}) {
  const tracking = useCartCardTracking(product, attribution);
  const handleProductClick = () => {
    tracking.click();
    onProductClick();
  };
  const LinkComponent = linkComponent || Link;
  const target = getCartAddProduct(product);
  const price = getPriceProductBasicInfo(target ?? product);
  const currentPrice = target ? getProductEffectivePrice(target) : (price.salePrice ?? price.originalPrice);
  const href = extractPathname(product.productLink);
  return (
    <CartDiscoveryCard
      className={cn(layout === 'grid' && 'w-full min-w-0', className)}
      onImpression={tracking.impression}
      attribution={attribution}
      impressionKey={`${product.productId}:${attribution?.tab ?? ''}`}
    >
      <CartDiscoveryCard.Media>
        <LinkComponent
          href={href}
          onClick={handleProductClick}
          aria-label={product.name}
          className={cn(
            'flex h-[152px] items-center justify-center rounded-lg overflow-hidden bg-neutral-50',
            layout === 'grid' && 'h-[200px]',
          )}
        >
          <CartDiscoveryCard.Picture
            src={target?.productImageUrls[0] || product.productImageUrls[0]}
            name={product.name}
            className={layout === 'grid' ? 'top-0.5 h-[145px] w-[112px]' : undefined}
          />
        </LinkComponent>
        {layout === 'compact' && <CartDiscoveryCard.Discount current={currentPrice} original={price.originalPrice} />}
        <CartDiscoveryCard.Add
          target={target}
          currency={currency}
          onCartMutation={onCartMutation}
          attribution={attribution}
          analyticsParams={tracking.params}
          className={layout === 'grid' ? 'size-8 [&_svg]:w-5 [&_svg]:h-5' : undefined}
        />
      </CartDiscoveryCard.Media>
      <LinkComponent
        href={href}
        onClick={handleProductClick}
        className={cn(
          'mt-2 flex w-full min-w-0 flex-col items-start gap-1.5 text-left no-underline hover:no-underline',
          layout === 'grid' && 'gap-2',
        )}
      >
        <CartDiscoveryCard.Name
          brand={product.brand}
          name={product.name}
          packLabel={getCartProductPackLabel(product, target?.sku)}
          layout={layout}
        />
        <CartDiscoveryCard.Price
          currency={currency}
          current={currentPrice}
          original={price.originalPrice}
          layout={layout}
        />
        <CartDiscoveryCard.Rating rating={rating} layout={layout} />
      </LinkComponent>
    </CartDiscoveryCard>
  );
}
